import oracleApi from 'app-lib/apis/oracleApi'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import { AnyProvider } from 'app-lib/providerUtils'
import { chainConfigs } from 'bzx-common/src/config/appConfig'
import { AbiCoder, solidityPack } from 'ethers5/lib/utils'
import JSBI from 'jsbi'
import { BigNumber } from '@0x/utils'
import { Protocol } from '@uniswap/router-sdk'
import { CurrencyAmount, Percent, Token } from '@uniswap/sdk-core'
import { AlphaRouter, V3Route, V3RouteWithValidQuote } from '@uniswap/smart-order-router'
import { DexDataPayload, IUniRoute, TradeType } from './types'

async function getUniswapV3RouteOut(
  provider: AnyProvider,
  params: { srcToken: Asset; destToken: Asset; amountUsed: BigNumber }
): Promise<[string, BigNumber]> {
  const { amountUsed } = params
  let { srcToken, destToken } = params
  const result: [string, BigNumber] = ['g', new BigNumber('0')]
  const nProvider = provider.ethersProvider
  const chainId = provider.currentChainId
  const router = new AlphaRouter({ chainId: chainId, provider: nProvider })
  let tempAssetChange: [Asset, number] = [Asset.UNKNOWN, 0]
  const USDValue =
    srcToken == Asset.USDC || Asset.USDT || Asset.FRAX
      ? amountUsed.dividedBy(10 ** assetUtils.getDecimals(chainId, srcToken))
      : (
          await oracleApi.getSwapRateNew(
            provider,
            srcToken,
            chainConfigs[chainId].stablecoinForUsdRates
          )
        )
          .times(amountUsed)
          .dividedBy(10 ** assetUtils.getDecimals(chainId, srcToken))

  if (chainId == 42161) {
    //Stopgap fix to get around lower liquidity on USDT
    if (srcToken == Asset.USDT) {
      if (USDValue.gt(125000) && destToken != Asset.USDC) {
        tempAssetChange[0] = Asset.USDT
        srcToken = Asset.USDC
      }
    } else if (destToken == Asset.USDT) {
      if (USDValue.gt(125000) && srcToken != Asset.USDC) {
        tempAssetChange = [Asset.USDT, 1]
        destToken = Asset.USDC
      }
    }
  }

  const { address: srcAddress, decimals: srcDecimals } = assetUtils.getInfo(chainId, srcToken)
  const SRC = new Token(chainId, srcAddress, srcDecimals, srcToken, 'xxx')

  const { address: destAddress, decimals: destDecimals } = assetUtils.getInfo(chainId, destToken)
  const DEST = new Token(chainId, destAddress, destDecimals, destToken, 'yyy')

  const amountInFinal = CurrencyAmount.fromRawAmount(SRC, JSBI.BigInt(amountUsed.integerValue()))
  const key = [srcToken, destToken].sort((a, b) => a.localeCompare(b)).join('')
  const uniRouteSettings = chainConfigs[provider.currentChainId].uniRouteSettings[key]
  let v3PoolSelectionSettings: IUniRoute = {
    topN: 0,
    topNDirectSwaps: 2,
    topNTokenInOut: 2,
    topNSecondHop: 0,
    topNWithEachBaseToken: 0,
    topNWithBaseToken: 0,
  }
  if (uniRouteSettings) {
    v3PoolSelectionSettings = uniRouteSettings[0]
    if (USDValue.gt(100000)) {
      v3PoolSelectionSettings = uniRouteSettings[1]
    } else if (USDValue.gt(1000000)) {
      v3PoolSelectionSettings = uniRouteSettings[2]
    }
  }
  const swapRoute = await router.route(
    amountInFinal,
    DEST,
    TradeType.EXACT_INPUT,
    {
      recipient: '0x0000000000000000000000000000000000000001',
      slippageTolerance: new Percent(5, 100),
      deadline: Math.round(new Date().getTime() / 1000 + 100000),
    },
    {
      blockNumber: await nProvider.getBlockNumber(),
      protocols: [Protocol.V3],
      v3PoolSelection: v3PoolSelectionSettings,
      maxSwapsPerPath: 4,
      maxSplits: 5,
    }
  )

  if (!swapRoute) {
    throw new Error('No route found on the AMM')
  }

  const finalSwapList: [string, string, string, string, string][] = []
  let finalSwapOut = new BigNumber(0)
  for (const x in swapRoute.route) {
    const routes = <V3Route>swapRoute.route[x].route
    const set: (string | number)[] = []
    const set1: string[] = []
    for (let y = 0; y < routes.pools.length; y++) {
      if (y === 0) {
        set.push(routes.tokenPath[y].address)
        set1.push('address')
        set.push(routes.pools[y].fee.toString())
        set1.push('uint24')
        set.push(routes.tokenPath[y + 1].address)
        set1.push('address')
      } else {
        set.push(routes.pools[y].fee.toString())
        set1.push('uint24')
        set.push(routes.tokenPath[y + 1].address)
        set1.push('address')
      }
    }
    if (tempAssetChange[0] !== Asset.UNKNOWN) {
      if (tempAssetChange[1] === 0) {
        set.unshift(assetUtils.getAddress(chainId, tempAssetChange[0]), '500')
        set1.unshift('address', 'uint24')
      } else {
        set.push('500', assetUtils.getAddress(chainId, tempAssetChange[0]))
        set1.push('uint24', 'address')
      }
    }
    finalSwapList.push([
      solidityPack(set1, set),
      '0x0000000000000000000000000000000000000001',
      Math.round(new Date().getTime() / 1000 + 100000).toString(),
      amountUsed.times(swapRoute.route[x].percent).div(101).integerValue().toString(),
      new BigNumber((<V3RouteWithValidQuote>swapRoute.route[x]).rawQuote.toString())
        .times(new BigNumber(0.99))
        .integerValue()
        .toString(),
    ])
    finalSwapOut = finalSwapOut.plus(
      new BigNumber((<V3RouteWithValidQuote>swapRoute.route[x]).rawQuote.toString())
    )
  }

  const abiCoder = new AbiCoder()
  result[0] = abiCoder.encode(
    ['uint256', 'bytes[]'],
    [
      2,
      [
        abiCoder.encode(
          ['uint256', 'bytes'],
          [2, abiCoder.encode(['(bytes,address,uint256,uint256,uint256)[]'], [finalSwapList])]
        ),
      ],
    ]
  )
  result[1] = finalSwapOut
  return result
}

async function getUniswapV3RouteIn(
  provider: AnyProvider,
  params: { srcToken: Asset; destToken: Asset; amountUsed: BigNumber; amountsInMax?: BigNumber }
): Promise<[string, BigNumber]> {
  const { amountUsed, amountsInMax } = params
  let srcToken = params.srcToken
  let destToken = params.destToken
  const result: [string, BigNumber] = ['0x', new BigNumber(0)]
  const nProvider = provider.ethersProvider
  const chainId = provider.currentChainId
  const router = new AlphaRouter({ chainId: chainId, provider: nProvider })
  let tempAssetChange: [Asset, number] = [Asset.UNKNOWN, 0]
  const USDValue =
    destToken == Asset.USDC || Asset.USDT || Asset.FRAX
      ? amountUsed.dividedBy(10 ** assetUtils.getDecimals(chainId, destToken))
      : (
          await oracleApi.getSwapRateNew(
            provider,
            destToken,
            chainConfigs[chainId].stablecoinForUsdRates
          )
        )
          .times(amountUsed)
          .dividedBy(10 ** assetUtils.getDecimals(chainId, destToken))
  if (chainId === 42161) {
    //Stopgap fix to get around lower liquidity on USDT
    if (srcToken == Asset.USDT) {
      if (USDValue.gt(125000) && destToken !== Asset.USDC) {
        tempAssetChange[0] = Asset.USDT
        srcToken = Asset.USDC
      }
    } else if (destToken == Asset.USDT) {
      if (USDValue.gt(125000) && srcToken !== Asset.USDC) {
        tempAssetChange = [Asset.USDT, 1]
        destToken = Asset.USDC
      }
    }
  }
  const { address: srcAddress, decimals: srcDecimals } = assetUtils.getInfo(chainId, srcToken)
  const SRC = new Token(chainId, srcAddress, srcDecimals, srcToken, 'xxx')

  const { address: destAddress, decimals: destDecimals } = assetUtils.getInfo(chainId, destToken)
  const DEST = new Token(chainId, destAddress, destDecimals, destToken, 'yyy')

  const amountInFinal = CurrencyAmount.fromRawAmount(DEST, JSBI.BigInt(amountUsed.integerValue()))
  const key = [srcToken, destToken].sort((a, b) => a.localeCompare(b)).join('')
  const uniRouteSettings = chainConfigs[provider.currentChainId].uniRouteSettings[key]
  let v3PoolSelectionSettings: IUniRoute = {
    topN: 0,
    topNDirectSwaps: 2,
    topNTokenInOut: 2,
    topNSecondHop: 0,
    topNWithEachBaseToken: 0,
    topNWithBaseToken: 0,
  }
  if (uniRouteSettings) {
    v3PoolSelectionSettings = uniRouteSettings[0]
    if (USDValue.gt(100000)) {
      v3PoolSelectionSettings = uniRouteSettings[1]
    } else if (USDValue.gt(1000000)) {
      v3PoolSelectionSettings = uniRouteSettings[2]
    }
  }

  const swapRoute = await router.route(
    amountInFinal,
    SRC,
    TradeType.EXACT_OUTPUT,
    {
      recipient: '0x0000000000000000000000000000000000000001',
      slippageTolerance: new Percent(5, 100),
      deadline: Math.round(new Date().getTime() / 1000 + 100000),
    },
    {
      blockNumber: await nProvider.getBlockNumber(),
      protocols: [Protocol.V3],
      v3PoolSelection: v3PoolSelectionSettings,
      maxSwapsPerPath: 4,
      maxSplits: 5,
    }
  )

  if (!swapRoute) {
    throw new Error('No route found on the AMM')
  }

  const finalSwapList: [string, string, string, string, string][] = []
  let finalSwapIn = new BigNumber(0)
  for (const x in swapRoute.route) {
    const routes = <V3Route>swapRoute.route[x].route
    const set: (string | number)[] = []
    const set1: string[] = []
    for (let y = 0; y < routes.pools.length; y++) {
      if (y === 0) {
        set.push(routes.tokenPath[y].address)
        set1.push('address')
        set.push(routes.pools[y].fee.toString())
        set1.push('uint24')
        set.push(routes.tokenPath[y + 1].address)
        set1.push('address')
      } else {
        set.push(routes.pools[y].fee.toString())
        set1.push('uint24')
        set.push(routes.tokenPath[y + 1].address)
        set1.push('address')
      }
    }
    let amountsIn: string = swapRoute.route[x].rawQuote.toBigInt().toString()
    if (amountsInMax) {
      amountsIn = amountsInMax.times(swapRoute.route[x].percent).div(100).integerValue().toString()
    }
    if (tempAssetChange[0] !== Asset.UNKNOWN) {
      if (tempAssetChange[1] === 0) {
        set.unshift(assetUtils.getAddress(chainId, tempAssetChange[0]), '500')
        set1.unshift('address', 'uint24')
      } else {
        set.push('500', assetUtils.getAddress(chainId, tempAssetChange[0]))
        set1.push('uint24', 'address')
      }
    }
    finalSwapList.push([
      solidityPack(set1, set.reverse()),
      '0x0000000000000000000000000000000000000001',
      Math.round(new Date().getTime() / 1000 + 100000).toString(),
      amountUsed.times(swapRoute.route[x].percent).div(100).integerValue().toString(),
      amountsIn,
    ])
    finalSwapIn = finalSwapIn.plus(
      new BigNumber((<V3RouteWithValidQuote>swapRoute.route[x]).rawQuote.toString())
    )
  }

  const abiCoder = new AbiCoder()
  result[0] = abiCoder.encode(
    ['uint256', 'bytes[]'],
    [
      2,
      [
        abiCoder.encode(
          ['uint256', 'bytes'],
          [2, abiCoder.encode(['(bytes,address,uint256,uint256,uint256)[]'], [finalSwapList])]
        ),
      ],
    ]
  )
  result[1] = finalSwapIn
  return result
}

async function getUniswapV3Route(provider: AnyProvider, payload: DexDataPayload) {
  return payload.direction === TradeType.EXACT_INPUT
    ? getUniswapV3RouteOut(provider, payload)
    : getUniswapV3RouteIn(provider, payload)
}

function restructureRoute(route: string): string {
  route = route.slice(2)
  const finalResult: string[] = []
  const encoderConditions: string[] = []
  let condition = true
  let retrieveAddress = true
  while (condition) {
    if (retrieveAddress) {
      finalResult.push('0x' + route.slice(0, 40))
      route = route.slice(40)
      retrieveAddress = false
      encoderConditions.push('address')
    } else {
      finalResult.push(parseInt(route.slice(0, 6), 16).toString())
      route = route.slice(6)
      retrieveAddress = true
      encoderConditions.push('uint24')
    }
    if (route.length === 0) {
      condition = false
    }
  }
  finalResult.reverse()
  encoderConditions.reverse()
  return solidityPack(encoderConditions, finalResult)
}

//Note: Only use for getSwapExpectedReturn or dexAmountOutFormatted. Do not use this for actual swap payload data
function convertInToOutUniV3(abiCoder: AbiCoder, payload: string): string {
  const passedData = abiCoder.decode(['(bytes,address,uint256,uint256,uint256)[]'], payload)[0]
  const dataToEncode: string[][] = []
  for (let i = 0; i < passedData.length; i++) {
    const listing: string[] = [
      restructureRoute(passedData[i][0]),
      passedData[i][1].toString(),
      passedData[i][2].toString(),
      passedData[i][4].toString(),
      passedData[i][3].toString(),
    ]
    dataToEncode.push(listing)
  }
  return abiCoder.encode(['(bytes,address,uint256,uint256,uint256)[]'], [dataToEncode])
}

export default {
  getUniswapV3RouteIn,
  getUniswapV3RouteOut,
  getUniswapV3Route,
  convertInToOutUniV3,
}
